import type { TextSearchQuery as TextSearchQueryOutput } from "openapi/shop_agent/client/types.gen";

export interface BrowseMenuItem {
  uuid: string;
  display_name: string;
  children?: Array<string> | null;
  search_query: TextSearchQueryOutput;
}

export interface BrowseMenuNode extends BrowseMenuItem {
  children_nodes?: BrowseMenuNode[];
  display_path: string[];
}

export interface BrowseMenuIndex {
  roots: BrowseMenuNode[];
  byId: Map<string, BrowseMenuNode>;
  byPath: Map<string, BrowseMenuNode>;
}

function pathKey(parts: string[]): string {
  return parts
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" > ");
}

export function buildBrowseMenuIndex(items: BrowseMenuItem[]): BrowseMenuIndex {
  const byId = new Map<string, BrowseMenuNode>();
  const childIds = new Set<string>();

  items.forEach((item) => {
    byId.set(item.uuid, { ...item, children_nodes: undefined, display_path: [] });
    (item.children || []).forEach((childId: string) => childIds.add(childId));
  });

  items.forEach((item) => {
    const node = byId.get(item.uuid);
    if (!node) return;
    if (item.children && item.children.length > 0) {
      node.children_nodes = item.children
        .map((childId: string) => byId.get(childId))
        .filter((child: BrowseMenuNode | undefined): child is BrowseMenuNode => Boolean(child));
    }
  });

  const ROOT_DISPLAY_ORDER: Record<string, number> = {
    Menswear: 0,
    Womenswear: 1,
    Kidswear: 2,
    Beauty: 3,
  };

  const roots: BrowseMenuNode[] = items
    .filter((item) => !childIds.has(item.uuid))
    .map((item) => byId.get(item.uuid))
    .filter((node): node is BrowseMenuNode => Boolean(node));

  roots.sort((a, b) => {
    const orderA = ROOT_DISPLAY_ORDER[a.display_name] ?? Number.MAX_SAFE_INTEGER;
    const orderB = ROOT_DISPLAY_ORDER[b.display_name] ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });

  const byPath = new Map<string, BrowseMenuNode>();

  const visit = (node: BrowseMenuNode, parentPath: string[]) => {
    const currentPath = [...parentPath, node.display_name];
    node.display_path = currentPath;
    byPath.set(pathKey(currentPath), node);
    (node.children_nodes || []).forEach((child) => visit(child, currentPath));
  };

  roots.forEach((root) => visit(root, []));

  return { roots, byId, byPath };
}

export function getNodeByBrowseMenuUuid(
  index: BrowseMenuIndex,
  uuid: string | null | undefined
): BrowseMenuNode | null {
  if (!uuid) return null;
  const directMatch = index.byId.get(uuid);
  if (directMatch) return directMatch;

  if (uuid.includes("::")) {
    const parts = uuid.split("::").filter(Boolean);
    const lastSegment = parts[parts.length - 1];
    if (lastSegment) {
      const suffixMatch = index.byId.get(lastSegment);
      if (suffixMatch) return suffixMatch;
    }
  }

  const tokens = uuid
    .split(/[^0-9a-fA-F-]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  for (const token of tokens) {
    const tokenMatch = index.byId.get(token);
    if (tokenMatch) return tokenMatch;
  }

  return null;
}

export function getNodeByDisplayPath(
  index: BrowseMenuIndex,
  displayPath: string | string[]
): BrowseMenuNode | null {
  const parts = Array.isArray(displayPath)
    ? displayPath
    : displayPath.split(" > ").map((part) => part.trim());
  const key = pathKey(parts);
  return index.byPath.get(key) || null;
}

export interface BrowseMenuNavigationPayload {
  facets: Array<{ name: string; values: string[] }>;
  display_path: string[];
  navbar_category: string;
  brand?: string;
}
